import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Info, Megaphone, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  adBlocksApi,
  spotsApi,
  type AlternatingAdsConfig,
  type AlternatingAdsMode,
  type AlternatingAdsPlacement,
  type AlternatingAdsPreview,
  type AlternatingAdsSkipReason,
} from '../../lib/adsApi.js'
import { addBroadcastDays, broadcastDayKey, broadcastDayLabel, broadcastDayWindow, broadcastTimeOfDay } from '../../lib/broadcast-day.js'
import { weekMondayISO } from '../../lib/utils.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'
import { alternatingSpotLabel } from '../../lib/alternatingAds.js'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert.js'
import { Badge } from '../ui/badge.js'
import { Button } from '../ui/button.js'
import { Checkbox } from '../ui/checkbox.js'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.js'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '../ui/field.js'
import { Input } from '../ui/input.js'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select.js'
import { Separator } from '../ui/separator.js'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js'
import { Spinner } from '../common/Spinner.js'
import './AgendaDialogs.css'

interface CommercialSpot {
  id: string
  title: string
  durationSec: number
  active: boolean
  campaign?: { id: string; name: string; startDate?: string; endDate?: string } | null
}

interface AlternatingAdDialogProps {
  channelId: string
  gridId: string
  date: string
  onClose: () => void
  onApplied: (created: number) => void
}

const REASON_LABELS: Record<AlternatingAdsSkipReason, string> = {
  INFOSERVICE_TOO_SHORT: 'Infoservice trop court',
  FROZEN: 'Déjà engagé à l’antenne',
  CONFLICT: 'Conflit dans la grille',
  SOURCE_MISSING: 'Média infoservice absent',
  ALREADY_PRESENT: 'Déjà dans la grille',
  BLOCK_LOCKED: 'Bloc à durée verrouillée',
}

function durationLabel(seconds: number): string {
  if (seconds < 60) return `${seconds} s`
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return remainder ? `${minutes} min ${remainder} s` : `${minutes} min`
}

function timeLabel(iso: string): string {
  return broadcastTimeOfDay(new Date(iso)).slice(0, 5)
}

export const AlternatingAdDialog = memo(function AlternatingAdDialog({
  channelId,
  gridId,
  date,
  onClose,
  onApplied,
}: AlternatingAdDialogProps) {
  const monday = useMemo(() => weekMondayISO(date), [date])
  const sunday = useMemo(() => broadcastDayKey(addBroadcastDays(monday, 6)), [monday])

  const autoPreviewRequested = useRef(false)
  const [spots, setSpots] = useState<CommercialSpot[]>([])
  const [spotIds, setSpotIds] = useState<string[]>([])
  const [firstSpotId, setFirstSpotId] = useState('')
  const [mode, setMode] = useState<AlternatingAdsMode>('ALTERNATE')
  const [singleSpotId, setSingleSpotId] = useState('')
  const [placement, setPlacement] = useState<AlternatingAdsPlacement>('BEFORE')
  const [selectedDate, setSelectedDate] = useState(() => broadcastDayKey(date))
  const [preview, setPreview] = useState<AlternatingAdsPreview | null>(null)
  const [selectedTargetKeys, setSelectedTargetKeys] = useState<string[]>([])
  const [loadingSpots, setLoadingSpots] = useState(true)
  const [calculating, setCalculating] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoadingSpots(true)
    spotsApi.list({ channelId, spotKind: 'COMMERCIAL' })
      .then((response: CommercialSpot[]) => {
        if (cancelled) return
        const available = response.filter(spot => spot.active)
        setSpots(available)
        // La sélection initiale est semée par l'effet de réconciliation ci-dessous,
        // qui ne retient que les spots éligibles pour la journée choisie.
        if (available.length < 1) setError('Ajoutez au moins un spot publicitaire actif avant de préparer un placement.')
      })
      .catch((cause: any) => {
        if (!cancelled) setError(cause?.message ?? 'Impossible de charger les spots publicitaires.')
      })
      .finally(() => { if (!cancelled) setLoadingSpots(false) })
    return () => { cancelled = true }
  }, [channelId])

  // Ne propose que les spots dont la campagne couvre la journée d'antenne
  // choisie, comme l'auto-composition côté serveur (ad-block.service.ts,
  // OUT_OF_PERIOD). Les spots standalone sans campagne restent toujours offerts.
  const eligibleSpots = useMemo(() => {
    const { from, to } = broadcastDayWindow(selectedDate)
    return spots.filter(spot => {
      if (!spot.campaign?.startDate || !spot.campaign?.endDate) return true
      const start = new Date(spot.campaign.startDate)
      const end = new Date(spot.campaign.endDate)
      return !(start > to || end < from)
    })
  }, [spots, selectedDate])

  // Réconcilie la sélection quand la liste éligible change (chargement ou
  // changement de date) : on retire les spots hors période et on complète
  // jusqu'à deux pour garder une rotation valide.
  useEffect(() => {
    const eligibleIds = eligibleSpots.map(spot => spot.id)
    const eligibleSet = new Set(eligibleIds)
    setSpotIds(current => {
      const next = current.filter(id => eligibleSet.has(id))
      for (const id of eligibleIds) {
        if (next.length >= 2) break
        if (!next.includes(id)) next.push(id)
      }
      const unchanged = next.length === current.length && next.every((id, i) => id === current[i])
      return unchanged ? current : next
    })
    setFirstSpotId(current => (eligibleSet.has(current) ? current : (eligibleIds[0] ?? '')))
    setSingleSpotId(current => (eligibleSet.has(current) ? current : (eligibleIds[0] ?? '')))
  }, [eligibleSpots])

  const config = useMemo<AlternatingAdsConfig | null>(() => {
    if (!selectedDate) return null
    if (mode === 'SINGLE') {
      if (!singleSpotId) return null
      return {
        channelId,
        gridId,
        date: selectedDate,
        spotIds: [singleSpotId],
        firstSpotId: singleSpotId,
        mode: 'SINGLE',
        placement,
      }
    }
    if (spotIds.length < 2 || new Set(spotIds).size !== spotIds.length || !firstSpotId || !spotIds.includes(firstSpotId)) return null
    return {
      channelId,
      gridId,
      date: selectedDate,
      spotIds,
      firstSpotId,
      mode: 'ALTERNATE',
    }
  }, [channelId, firstSpotId, gridId, mode, placement, selectedDate, singleSpotId, spotIds])

  const calculatePreview = useCallback(async () => {
    if (!config) {
      setError('Choisissez au moins deux spots différents et une journée.')
      return
    }
    setCalculating(true)
    setError(null)
    try {
      const result = await adBlocksApi.previewAlternating(config)
      setPreview(result)
      // Un ajout à un bloc qui porte déjà un spot choisi arrive décoché : un
      // recalcul ne doit pas rallonger deux fois le même bloc tout seul.
      setSelectedTargetKeys(result.targets.filter(target => target.possible && target.recommended).map(target => target.targetKey))
    } catch (cause: any) {
      setPreview(null)
      setSelectedTargetKeys([])
      setError(cause?.message ?? 'Impossible de calculer les insertions.')
    } finally {
      setCalculating(false)
    }
  }, [config])

  useEffect(() => {
    if (!loadingSpots && config && !autoPreviewRequested.current) {
      autoPreviewRequested.current = true
      calculatePreview().catch(console.error)
    }
  }, [calculatePreview, config, loadingSpots])

  const invalidatePreview = useCallback(() => {
    setPreview(null)
    setSelectedTargetKeys([])
    setError(null)
  }, [])

  const handleSpotChange = useCallback((index: number, value: string) => {
    const previousId = spotIds[index]
    setSpotIds(current => current.map((id, candidateIndex) => candidateIndex === index ? value : id))
    if (firstSpotId === previousId) setFirstSpotId(value)
    invalidatePreview()
  }, [firstSpotId, invalidatePreview, spotIds])
  const handleAddSpot = useCallback(() => {
    setSpotIds(current => {
      const nextSpot = eligibleSpots.find(spot => !current.includes(spot.id))
      return nextSpot ? [...current, nextSpot.id] : current
    })
    invalidatePreview()
  }, [invalidatePreview, eligibleSpots])
  const handleRemoveSpot = useCallback((index: number) => {
    if (spotIds.length <= 2) return
    const removedId = spotIds[index]
    const next = spotIds.filter((_, candidateIndex) => candidateIndex !== index)
    setSpotIds(next)
    if (firstSpotId === removedId) setFirstSpotId(next[0] ?? '')
    invalidatePreview()
  }, [firstSpotId, invalidatePreview, spotIds])
  const handleFirstSpotChange = useCallback((value: string) => {
    setFirstSpotId(value)
    invalidatePreview()
  }, [invalidatePreview])
  const handleModeChange = useCallback((value: AlternatingAdsMode) => {
    setMode(value)
    invalidatePreview()
  }, [invalidatePreview])
  const handlePlacementChange = useCallback((value: AlternatingAdsPlacement) => {
    setPlacement(value)
    invalidatePreview()
  }, [invalidatePreview])
  const handleSingleSpotChange = useCallback((value: string) => {
    setSingleSpotId(value)
    invalidatePreview()
  }, [invalidatePreview])

  const alternationOrder = useMemo(() => {
    const firstIndex = spotIds.indexOf(firstSpotId)
    if (firstIndex < 0) return ''
    const orderedIds = [...spotIds.slice(firstIndex), ...spotIds.slice(0, firstIndex)]
    return orderedIds.map(id => alternatingSpotLabel(spotIds.indexOf(id))).join(', ')
  }, [firstSpotId, spotIds])
  const canAddSpot = eligibleSpots.some(spot => !spotIds.includes(spot.id))
  const spotById = useMemo(() => new Map(spots.map(spot => [spot.id, spot])), [spots])
  const selectedDateLabel = useMemo(() => (
    broadcastDayLabel(selectedDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  ), [selectedDate])
  const handleDateChange = useCallback((value: string) => {
    setSelectedDate(value)
    invalidatePreview()
  }, [invalidatePreview])

  const applicableKeys = useMemo(
    () => preview?.targets.filter(target => target.possible).map(target => target.targetKey) ?? [],
    [preview],
  )
  const allSelected = applicableKeys.length > 0 && applicableKeys.every(key => selectedTargetKeys.includes(key))
  const someSelected = selectedTargetKeys.length > 0 && !allSelected

  const handleToggleAll = useCallback((checked: boolean | 'indeterminate') => {
    setSelectedTargetKeys(checked === true ? applicableKeys : [])
  }, [applicableKeys])
  const handleToggleTarget = useCallback((targetKey: string, checked: boolean | 'indeterminate') => {
    setSelectedTargetKeys(current => checked === true
      ? [...new Set([...current, targetKey])]
      : current.filter(key => key !== targetKey))
  }, [])

  const handleApply = useCallback(async () => {
    if (!config || selectedTargetKeys.length === 0) return
    setApplying(true)
    setError(null)
    try {
      const result = await recordGridChange({
        label: `alternance publicitaire sur ${selectedTargetKeys.length} infoservice(s)`,
        gridIds: [gridId],
        date: selectedDate,
      }, () => adBlocksApi.applyAlternating({ ...config, selectedTargetKeys }))
      onApplied(result.created)
      onClose()
    } catch (cause: any) {
      setError(cause?.message ?? 'Impossible d’appliquer l’alternance publicitaire.')
    } finally {
      setApplying(false)
    }
  }, [config, gridId, onApplied, onClose, selectedDate, selectedTargetKeys])

  return (
    <Dialog open onOpenChange={open => { if (!open && !applying) onClose() }}>
      <DialogContent className="alternating-ads-dialog">
        <DialogHeader className="alternating-ads-dialog__header">
          <DialogTitle>Placer des spots en alternance</DialogTitle>
          <DialogDescription>
            {mode === 'SINGLE'
              ? 'Poser un seul spot avant ou après un infoservice, sans décaler les programmes suivants.'
              : 'Placer une rotation de spots avant les infoservices, sans décaler les programmes suivants.'}
          </DialogDescription>
        </DialogHeader>

        <div className="alternating-ads-dialog__body">
          <section className="alternating-ads-dialog__config" aria-label="Règles de placement">
            <div className="alternating-ads-dialog__mode-switch" role="group" aria-label="Type de placement">
              <Button
                type="button"
                size="sm"
                variant={mode === 'ALTERNATE' ? 'default' : 'outline'}
                aria-pressed={mode === 'ALTERNATE'}
                onClick={() => handleModeChange('ALTERNATE')}
              >
                Alternance
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === 'SINGLE' ? 'default' : 'outline'}
                aria-pressed={mode === 'SINGLE'}
                onClick={() => handleModeChange('SINGLE')}
              >
                Spot unique
              </Button>
            </div>

            <div className="alternating-ads-dialog__section-heading">
              <span className="alternating-ads-dialog__step" aria-hidden="true">1</span>
              <div>
                <h3>{mode === 'SINGLE' ? 'Spot publicitaire' : 'Spots publicitaires'}</h3>
                <p>{mode === 'SINGLE'
                  ? 'Choisissez le média à poser sur la journée.'
                  : 'Composez une rotation d’au moins 2 médias actifs, sans limite de nombre.'}</p>
              </div>
            </div>

            {!loadingSpots && spots.length > 0 && eligibleSpots.length === 0 && (
              <Alert className="alternating-ads-dialog__error">
                <Info />
                <AlertTitle>Aucun spot actif à cette date</AlertTitle>
                <AlertDescription>
                  Aucune campagne n’est en période de validité le {selectedDateLabel}. Choisissez une autre journée.
                </AlertDescription>
              </Alert>
            )}

            {mode === 'SINGLE' ? (
              <Field>
                <FieldLabel htmlFor="alternating-single-spot">Spot à placer</FieldLabel>
                <Select value={singleSpotId} onValueChange={handleSingleSpotChange} disabled={loadingSpots}>
                  <SelectTrigger id="alternating-single-spot" className="alternating-ads-dialog__select-trigger">
                    <SelectValue placeholder="Choisir un spot" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="alternating-ads-select-content">
                    <SelectGroup>
                      {eligibleSpots.map(spot => (
                        <SelectItem key={spot.id} value={spot.id}>
                          {formatAdSpotLabel(spot)} · {durationLabel(spot.durationSec)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldDescription>Aucune rotation : le même spot est proposé sur chaque cible.</FieldDescription>
              </Field>
            ) : (
            <>
            <FieldGroup className="alternating-ads-dialog__spot-fields">
              {spotIds.map((spotId, index) => (
                <Field key={spotId} orientation="horizontal" className="alternating-ads-dialog__spot-row">
                  <FieldLabel htmlFor={`alternating-spot-${index}`}>
                    <Badge variant={index === 0 ? 'default' : 'secondary'}>{alternatingSpotLabel(index)}</Badge>
                    <span className="sr-only">Spot {alternatingSpotLabel(index)}</span>
                  </FieldLabel>
                  <Select value={spotId} onValueChange={value => handleSpotChange(index, value)} disabled={loadingSpots}>
                    <SelectTrigger id={`alternating-spot-${index}`} className="alternating-ads-dialog__select-trigger">
                      <SelectValue placeholder="Choisir un spot" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="alternating-ads-select-content">
                      <SelectGroup>
                        {eligibleSpots.map(spot => (
                          <SelectItem key={spot.id} value={spot.id} disabled={spot.id !== spotId && spotIds.includes(spot.id)}>
                            {formatAdSpotLabel(spot)} · {durationLabel(spot.durationSec)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={spotIds.length <= 2}
                    onClick={() => handleRemoveSpot(index)}
                    aria-label={`Retirer le spot ${alternatingSpotLabel(index)} de l’alternance`}
                  >
                    <Trash2 />
                  </Button>
                </Field>
              ))}
            </FieldGroup>
            <Button type="button" variant="ghost" size="sm" className="alternating-ads-dialog__add-spot" onClick={handleAddSpot} disabled={!canAddSpot}>
              <Plus data-icon="inline-start" />
              Ajouter un spot
            </Button>
            </>
            )}

            <Separator />

            <div className="alternating-ads-dialog__section-heading">
              <span className="alternating-ads-dialog__step" aria-hidden="true">2</span>
              <div>
                <h3>{mode === 'SINGLE' ? 'Position du spot' : 'Règle d’alternance'}</h3>
                <p>{mode === 'SINGLE'
                  ? 'Avant ou après l’infoservice, en réduisant sa durée.'
                  : 'L’ordre se poursuit sur toute la semaine.'}</p>
              </div>
            </div>

            {mode === 'SINGLE' ? (
              <Field>
                <FieldLabel>Placement</FieldLabel>
                <div className="alternating-ads-dialog__placement-switch" role="group" aria-label="Placement du spot">
                  <Button
                    type="button"
                    size="sm"
                    variant={placement === 'BEFORE' ? 'default' : 'outline'}
                    aria-pressed={placement === 'BEFORE'}
                    onClick={() => handlePlacementChange('BEFORE')}
                  >
                    Avant l’infoservice
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={placement === 'AFTER' ? 'default' : 'outline'}
                    aria-pressed={placement === 'AFTER'}
                    onClick={() => handlePlacementChange('AFTER')}
                  >
                    Après l’infoservice
                  </Button>
                </div>
                <FieldDescription>
                  {placement === 'BEFORE'
                    ? 'Le spot prend le début de la plage ; un espace libre juste avant est utilisé en priorité.'
                    : 'Le spot prend la fin de la plage ; un espace libre juste après est utilisé en priorité.'}
                </FieldDescription>
              </Field>
            ) : (
            <Field>
              <FieldLabel htmlFor="alternating-first-spot">Premier passage</FieldLabel>
              <Select value={firstSpotId} onValueChange={handleFirstSpotChange} disabled={spotIds.length < 2}>
                <SelectTrigger id="alternating-first-spot" className="alternating-ads-dialog__select-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="alternating-ads-select-content">
                  <SelectGroup>
                    {spotIds.map((spotId, index) => (
                      <SelectItem key={spotId} value={spotId}>
                        Commencer par {alternatingSpotLabel(index)} · {spotById.has(spotId) ? formatAdSpotLabel(spotById.get(spotId)!) : 'Spot'}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FieldDescription>
                {alternationOrder ? `${alternationOrder}, ${alternatingSpotLabel(spotIds.indexOf(firstSpotId))}…` : 'Ordre de rotation'} · Les blocs existants sont pris en compte.
              </FieldDescription>
            </Field>
            )}

            <Separator />

            <div className="alternating-ads-dialog__section-heading">
              <span className="alternating-ads-dialog__step" aria-hidden="true">3</span>
              <div>
                <h3>Journée à scanner</h3>
                <p>La journée complète est analysée automatiquement.</p>
              </div>
            </div>

            <Field className="alternating-ads-dialog__date-field">
              <FieldLabel htmlFor="alternating-date">Date de diffusion</FieldLabel>
              <Input
                id="alternating-date"
                type="date"
                min={monday}
                max={sunday}
                value={selectedDate}
                onChange={event => handleDateChange(event.target.value)}
              />
              <FieldDescription>
                {selectedDateLabel} · scan de 00:00 à 24:00.
              </FieldDescription>
            </Field>
          </section>

          <section className="alternating-ads-dialog__preview" aria-label="Prévisualisation des insertions">
            <div className="alternating-ads-dialog__preview-header">
              <div>
                <h3>Insertions proposées</h3>
                <p>Les spots déjà présents sont détectés et ne seront pas insérés une seconde fois.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => { calculatePreview().catch(console.error) }} disabled={!config || calculating || applying}>
                {calculating ? <Spinner /> : <RefreshCw data-icon="inline-start" />}
                Recalculer
              </Button>
            </div>

            {error && (
              <Alert variant="destructive" className="alternating-ads-dialog__error">
                <AlertCircle />
                <AlertTitle>Action impossible</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {calculating && !preview ? (
              <div className="alternating-ads-dialog__loading" role="status">
                <Spinner /> Calcul des fins d’infoservice…
              </div>
            ) : preview && preview.targets.length > 0 ? (
              <div className="alternating-ads-dialog__table-wrap">
                <Table className="alternating-ads-dialog__table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="alternating-ads-dialog__check-cell">
                        <Checkbox
                          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                          onCheckedChange={handleToggleAll}
                          disabled={applicableKeys.length === 0}
                          aria-label="Sélectionner toutes les insertions possibles"
                        />
                      </TableHead>
                      <TableHead>Origine</TableHead>
                      <TableHead>Spot posé</TableHead>
                      <TableHead>État</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.targets.map(target => (
                      <TableRow key={target.targetKey} data-state={selectedTargetKeys.includes(target.targetKey) ? 'selected' : undefined}>
                        <TableCell className="alternating-ads-dialog__check-cell">
                          <Checkbox
                            checked={selectedTargetKeys.includes(target.targetKey)}
                            onCheckedChange={checked => handleToggleTarget(target.targetKey, checked)}
                            disabled={!target.possible || applying}
                            aria-label={target.targetType === 'EXISTING'
                              ? `${formatAdSpotLabel({ ...target.spot, campaignName: spotById.get(target.spot.id)?.campaign?.name })} est déjà présent à ${timeLabel(target.startTime)}`
                              : `Insérer ${formatAdSpotLabel({ ...target.spot, campaignName: spotById.get(target.spot.id)?.campaign?.name })} à ${timeLabel(target.startTime)}`}
                          />
                        </TableCell>
                        <TableCell>
                          {target.targetType === 'EXISTING' ? (
                            <>
                              <div className="alternating-ads-dialog__cell-main">{timeLabel(target.startTime)} · Bloc publicitaire</div>
                              <div className="alternating-ads-dialog__cell-meta">Spot déjà planifié dans ce bloc</div>
                            </>
                          ) : target.targetType === 'APPEND' ? (
                            <>
                              <div className="alternating-ads-dialog__cell-main">{timeLabel(target.infoserviceStartTime)} · {target.infoserviceTitle}</div>
                              <div className="alternating-ads-dialog__cell-meta">
                                Ajouté au bloc pub collé {target.placement === 'BEFORE' ? 'avant' : 'après'} · plage réduite d’autant
                              </div>
                            </>
                          ) : target.targetType === 'GAP' ? (
                            <>
                              <div className="alternating-ads-dialog__cell-main">{timeLabel(target.startTime)} · Espace libre</div>
                              <div className="alternating-ads-dialog__cell-meta">
                                {target.placement === 'BEFORE' ? 'Collé avant un infoservice' : 'Collé après un infoservice'} · sans réduire la plage
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="alternating-ads-dialog__cell-main">{timeLabel(target.infoserviceStartTime)} · {target.infoserviceTitle}</div>
                              <div className="alternating-ads-dialog__cell-meta">
                                Plage de {durationLabel(target.infoserviceDurationSec)}
                                {mode === 'SINGLE' && ` · ${target.placement === 'BEFORE' ? 'spot au début' : 'spot à la fin'}`}
                              </div>
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="alternating-ads-dialog__spot-cell">
                            {mode === 'ALTERNATE' && spotIds.indexOf(target.spot.id) >= 0 && (
                              <Badge variant={spotIds.indexOf(target.spot.id) === 0 ? 'default' : 'secondary'}>
                                {alternatingSpotLabel(spotIds.indexOf(target.spot.id))}
                              </Badge>
                            )}
                            <span className="alternating-ads-dialog__spot-title">
                              {formatAdSpotLabel({ ...target.spot, campaignName: spotById.get(target.spot.id)?.campaign?.name })}
                            </span>
                            <span className="alternating-ads-dialog__cell-meta">{durationLabel(target.spot.durationSec)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {target.targetType === 'EXISTING'
                            ? <Badge variant="secondary">Déjà dans la grille</Badge>
                            : target.possible
                            ? <Badge variant="outline">Possible</Badge>
                            : <Badge variant="destructive">{REASON_LABELS[target.reason!]}</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : preview ? (
              <div className="tp-empty-state alternating-ads-dialog__empty">
                <span className="alternating-ads-dialog__empty-icon" aria-hidden="true"><Megaphone /></span>
                <h4>Aucun passage sur cette journée</h4>
                <p>Aucun début d’infoservice ni aucun spot sélectionné n’a été détecté le {selectedDateLabel}.</p>
                <Button type="button" variant="outline" size="sm" onClick={() => { calculatePreview().catch(console.error) }} disabled={!config || calculating}>
                  <RefreshCw data-icon="inline-start" />
                  Recalculer
                </Button>
              </div>
            ) : (
              <div className="tp-empty-state alternating-ads-dialog__empty">
                <span className="alternating-ads-dialog__empty-icon" aria-hidden="true"><Info /></span>
                <h4>Prévisualisation à recalculer</h4>
                <p>Les réglages ont changé. Recalculez les insertions avant de les appliquer.</p>
                <Button type="button" onClick={() => { calculatePreview().catch(console.error) }} disabled={!config || loadingSpots}>
                  Calculer les insertions
                </Button>
              </div>
            )}
          </section>
        </div>

        <DialogFooter className="alternating-ads-dialog__footer">
          <p className="alternating-ads-dialog__summary" aria-live="polite">
            {preview
              ? <><strong>{selectedTargetKeys.length}</strong> insertion(s) sélectionnée(s) <span>· {preview.existingCount} déjà présente(s) · {preview.ignoredCount} ignorée(s)</span></>
              : 'Aucune modification appliquée'}
          </p>
          <div className="alternating-ads-dialog__actions">
            <Button type="button" variant="outline" onClick={onClose} disabled={applying}>Annuler</Button>
            <Button type="button" onClick={() => { handleApply().catch(console.error) }} disabled={!preview || selectedTargetKeys.length === 0 || calculating || applying}>
              {applying && <Spinner />}
              Appliquer {selectedTargetKeys.length > 0 ? `(${selectedTargetKeys.length})` : ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})
